import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  DocumentAPI,
  uploadFileToS3,
} from "../../../services/document.service";
import type {
  DocumentCategory,
  DocumentListFilters,
  PatientDocument,
} from "../../../services/document.service";

// ─── Constants ────────────────────────────────────────────────────────────────

const ACCEPTED_TYPES = "application/pdf,image/jpeg,image/jpg,image/png,image/heic,image/heif,image/tiff,application/dicom,application/octet-stream";
const ACCEPTED_EXTS  = new Set([".pdf", ".jpg", ".jpeg", ".png", ".heic", ".heif", ".tiff", ".tif", ".dcm"]);
const MAX_BYTES      = 50 * 1024 * 1024;

const IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif", "image/tiff"]);
const PDF_TYPE    = "application/pdf";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function extOf(filename: string): string {
  const idx = filename.lastIndexOf(".");
  return idx >= 0 ? filename.slice(idx).toLowerCase() : "";
}

function mimeIcon(mime: string, filename: string): string {
  if (mime === PDF_TYPE) return "📄";
  if (IMAGE_TYPES.has(mime)) return "🖼️";
  if (mime === "application/dicom" || extOf(filename) === ".dcm") return "🩻";
  return "📎";
}

function validateFile(file: File): string | null {
  const ext = extOf(file.name);
  if (!ACCEPTED_EXTS.has(ext)) return `Unsupported file type: ${ext || "unknown"}`;
  if (file.size > MAX_BYTES) return `File too large: ${fmtSize(file.size)} (max 50 MB)`;
  return null;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface FileDraft {
  file:                 File;
  preview:              string | null;
  categoryId:           string;
  testDate:             string;
  sourceName:           string;
  orderingDoctorName:   string;
  notes:                string;
  tags:                 string;       // comma-separated, parsed on submit
  isSensitive:          boolean;
  progress:             number;       // 0-100, shown while uploading
  error:                string | null;
}

// ─── Upload Modal ─────────────────────────────────────────────────────────────

interface UploadModalProps {
  patientId:  string;
  visitId?:   string;
  categories: DocumentCategory[];
  onClose:    () => void;
  onUploaded: () => void;
}

export const DocumentUploadModal: React.FC<UploadModalProps> = ({ patientId, visitId, categories, onClose, onUploaded }) => {
  const [drafts, setDrafts]   = useState<FileDraft[]>([]);
  const [step, setStep]       = useState<"pick" | "meta" | "uploading">("pick");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Revoke all blob URLs when the modal unmounts to prevent memory leaks.
  useEffect(() => {
    return () => {
      setDrafts((current) => {
        current.forEach((d) => { if (d.preview) URL.revokeObjectURL(d.preview); });
        return current;
      });
    };
  }, []);

  const addFiles = useCallback((files: File[]) => {
    const valid: FileDraft[] = [];
    for (const f of files) {
      const err = validateFile(f);
      if (err) { toast.error(`${f.name}: ${err}`); continue; }
      const preview = IMAGE_TYPES.has(f.type) ? URL.createObjectURL(f) : null;
      valid.push({
        file: f, preview,
        categoryId: "", testDate: "", sourceName: "", orderingDoctorName: "",
        notes: "", tags: "",
        isSensitive: false, progress: 0, error: null,
      });
    }
    if (valid.length) {
      setDrafts((d) => [...d, ...valid]);
      if (step === "pick") setStep("meta");
    }
  }, [step]);

  // Clipboard paste — grab any images
  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const imageFiles = items.filter((i) => i.kind === "file" && i.type.startsWith("image/")).map((i) => i.getAsFile()).filter(Boolean) as File[];
      if (imageFiles.length) addFiles(imageFiles);
    };
    window.addEventListener("paste", handler);
    return () => window.removeEventListener("paste", handler);
  }, [addFiles]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const updateDraft = (idx: number, patch: Partial<FileDraft>) =>
    setDrafts((d) => d.map((f, i) => (i === idx ? { ...f, ...patch } : f)));

  const removeDraft = (idx: number) => {
    setDrafts((d) => {
      if (d[idx]?.preview) URL.revokeObjectURL(d[idx].preview!);
      const next = d.filter((_, i) => i !== idx);
      if (!next.length) setStep("pick");
      return next;
    });
  };

  const handleUpload = async () => {
    if (!drafts.length) return;
    setStep("uploading");

    try {
      // 1. Request presigned PUT URLs
      const res = await DocumentAPI.requestUploadUrls(
        patientId,
        drafts.map((d) => ({ file_name: d.file.name, mime_type: d.file.type || "application/octet-stream", file_size_bytes: d.file.size })),
      );

      // 2. PUT each file to S3 independently — allSettled so a single failure
      //    does not abort uploads that are already in flight or completed.
      const results = await Promise.allSettled(
        res.files.map(async (item, idx) => {
          if (item.upload_url) {
            await uploadFileToS3(item.upload_url, drafts[idx].file, (pct) =>
              updateDraft(idx, { progress: pct }),
            );
          }
          updateDraft(idx, { progress: 100 });
          return idx;
        }),
      );

      const succeededIndices = results
        .map((r, i) => (r.status === "fulfilled" ? i : -1))
        .filter((i) => i >= 0);
      const failCount = results.length - succeededIndices.length;

      if (failCount > 0) {
        toast.error(`${failCount} file${failCount > 1 ? "s" : ""} failed to upload`);
        // Mark failed drafts with an error so the user can see which ones need retry
        results.forEach((r, i) => {
          if (r.status === "rejected") {
            updateDraft(i, { error: (r.reason as Error)?.message ?? "Upload failed" });
          }
        });
      }

      // 3. Confirm only the files that reached S3 successfully.
      if (succeededIndices.length > 0) {
        await DocumentAPI.confirm(
          patientId,
          succeededIndices.map((idx) => {
            const item = res.files[idx];
            const d = drafts[idx];
            return {
              s3_key:          item.s3_key,
              file_name:       d.file.name,
              mime_type:       d.file.type || "application/octet-stream",
              file_size_bytes: d.file.size,
              upload_batch_id: res.upload_batch_id,
              category_id:          d.categoryId || null,
              visit_id:             visitId || null,
              test_date:            d.testDate || null,
              source_name:          d.sourceName.trim() || null,
              ordering_doctor_name: d.orderingDoctorName.trim() || null,
              notes:                d.notes.trim() || null,
              tags:                 d.tags.split(",").map((t) => t.trim()).filter(Boolean),
              is_sensitive:         d.isSensitive,
            };
          }),
        );
        toast.success(`${succeededIndices.length} document${succeededIndices.length > 1 ? "s" : ""} uploaded`);
        onUploaded();
        if (failCount === 0) onClose();
        else setStep("meta");  // leave modal open so user can see failed files
      } else {
        setStep("meta");
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed");
      setStep("meta");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Upload Documents</h2>
            {visitId && (
              <p className="text-xs text-indigo-600 mt-0.5">Attached to this visit</p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Drop zone (always visible to add more files) */}
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              dragOver ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <svg className="mx-auto mb-2 text-slate-400" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <p className="text-sm font-medium text-slate-700">
              {drafts.length ? "Add more files" : "Drop files here or click to select"}
            </p>
            <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, HEIC, TIFF, DICOM · Max 50 MB · Paste image from clipboard</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_TYPES}
              className="hidden"
              onChange={(e) => addFiles(Array.from(e.target.files ?? []))}
            />
          </div>

          {/* Per-file metadata rows */}
          {drafts.map((d, idx) => (
            <div key={idx} className="border border-slate-100 rounded-xl p-4 space-y-3">
              {/* File header */}
              <div className="flex items-start gap-3">
                {d.preview ? (
                  <img src={d.preview} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-slate-100" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-slate-50 flex items-center justify-center text-2xl flex-shrink-0">
                    {mimeIcon(d.file.type, d.file.name)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{d.file.name}</p>
                  <p className="text-xs text-slate-400">{fmtSize(d.file.size)}</p>
                  {step === "uploading" && (
                    <div className="mt-1.5 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                      <div className="bg-blue-500 h-full transition-all" style={{ width: `${d.progress}%` }} />
                    </div>
                  )}
                </div>
                {step !== "uploading" && (
                  <button onClick={() => removeDraft(idx)} className="text-slate-300 hover:text-red-400 transition-colors flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                )}
              </div>

              {/* Metadata fields */}
              {step !== "uploading" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Category</label>
                    <select
                      value={d.categoryId}
                      onChange={(e) => updateDraft(idx, { categoryId: e.target.value })}
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white"
                    >
                      <option value="">— Select —</option>
                      {categories.map((c) => (
                        <option key={c.category_id} value={c.category_id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Test / Document Date</label>
                    <input
                      type="date"
                      value={d.testDate}
                      onChange={(e) => updateDraft(idx, { testDate: e.target.value })}
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Source / Lab name</label>
                    <input
                      type="text"
                      value={d.sourceName}
                      onChange={(e) => updateDraft(idx, { sourceName: e.target.value })}
                      placeholder="e.g. City Diagnostics"
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Ordering Doctor</label>
                    <input
                      type="text"
                      value={d.orderingDoctorName}
                      onChange={(e) => updateDraft(idx, { orderingDoctorName: e.target.value })}
                      placeholder="e.g. Dr. Sharma"
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Tags <span className="font-normal">(comma-separated)</span></label>
                    <input
                      type="text"
                      value={d.tags}
                      onChange={(e) => updateDraft(idx, { tags: e.target.value })}
                      placeholder="e.g. ecg, follow-up"
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Notes</label>
                    <textarea
                      value={d.notes}
                      onChange={(e) => updateDraft(idx, { notes: e.target.value })}
                      rows={2}
                      placeholder="Any additional notes…"
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
                    />
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => updateDraft(idx, { isSensitive: !d.isSensitive })}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${d.isSensitive ? "bg-amber-500" : "bg-slate-200"}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${d.isSensitive ? "translate-x-4" : "translate-x-1"}`} />
                    </button>
                    <span className="text-xs text-slate-600">
                      {d.isSensitive ? (
                        <span className="text-amber-700 font-medium">Sensitive — restricted access</span>
                      ) : (
                        "Mark as sensitive"
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        {drafts.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={step === "uploading"}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={step === "uploading"}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {step === "uploading" ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                  Uploading…
                </>
              ) : (
                `Upload ${drafts.length} file${drafts.length > 1 ? "s" : ""}`
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Document Viewer ──────────────────────────────────────────────────────────

interface ViewerProps {
  doc:       PatientDocument;
  patientId: string;
  onClose:   () => void;
}

const DocumentViewer: React.FC<ViewerProps> = ({ doc, patientId, onClose }) => {
  const [url, setUrl]       = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom]     = useState(1);
  const [pan, setPan]       = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, px: 0, py: 0 });

  const isDicom = doc.mime_type === "application/dicom" || doc.file_name.endsWith(".dcm");
  const isImage = IMAGE_TYPES.has(doc.mime_type) && !isDicom;
  const isPdf   = doc.mime_type === PDF_TYPE;

  useEffect(() => {
    // Fetch presigned URL — use preview if available for images (HEIC/TIFF → JPEG)
    const fetchUrl = isPdf || isImage
      ? (doc.preview_s3_key ? DocumentAPI.getPreviewUrl(patientId, doc.document_id) : DocumentAPI.getUrl(patientId, doc.document_id))
      : DocumentAPI.getUrl(patientId, doc.document_id);

    fetchUrl.then((r) => { setUrl(r.url); setLoading(false); }).catch(() => setLoading(false));
  }, [doc.document_id, patientId, isPdf, isImage, doc.preview_s3_key]);

  // Mouse drag for pan
  const onMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setPan({ x: dragStart.current.px + e.clientX - dragStart.current.x, y: dragStart.current.py + e.clientY - dragStart.current.y });
  };
  const onMouseUp = () => setDragging(false);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-950" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Viewer toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900/80 backdrop-blur-sm flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{doc.file_name}</p>
            <p className="text-xs text-gray-400">
              {fmtSize(doc.file_size_bytes)} ·{" "}
              {doc.visit_id
                ? `Uploaded during visit on ${fmtDateTime(doc.visit_date || doc.uploaded_at)}`
                : `Uploaded on ${fmtDateTime(doc.uploaded_at || doc.test_date)}`}
            </p>
          </div>
          {doc.is_sensitive && (
            <span className="flex-shrink-0 text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-full">
              Sensitive
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isImage && (
            <>
              <button onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))} className="text-gray-400 hover:text-white text-lg leading-none px-2 py-1 hover:bg-white/10 rounded-lg transition-colors">−</button>
              <span className="text-xs text-gray-400 w-10 text-center">{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="text-gray-400 hover:text-white text-lg leading-none px-2 py-1 hover:bg-white/10 rounded-lg transition-colors">+</button>
              <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} className="text-xs text-gray-400 hover:text-white px-2 py-1 hover:bg-white/10 rounded-lg transition-colors">Reset</button>
            </>
          )}
          {url && (
            <a href={url} download={doc.file_name} className="text-xs bg-white/10 hover:bg-white/20 text-gray-200 px-3 py-1.5 rounded-lg transition-colors">
              Download
            </a>
          )}
        </div>
      </div>

      {/* Viewer body */}
      <div className="flex-1 overflow-hidden relative flex items-center justify-center">
        {loading && (
          <div className="flex flex-col items-center gap-3 text-gray-500">
            <svg className="animate-spin w-8 h-8" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
            <span className="text-sm">Loading…</span>
          </div>
        )}

        {!loading && !url && (
          <div className="text-center text-gray-500 space-y-2 p-8">
            <p className="text-lg">Preview unavailable</p>
            <p className="text-sm">The file may not be accessible or was not yet uploaded.</p>
          </div>
        )}

        {!loading && url && isPdf && (
          <iframe
            src={url}
            title={doc.file_name}
            className="w-full h-full border-0"
          />
        )}

        {!loading && url && isImage && (
          <div
            className="w-full h-full flex items-center justify-center overflow-hidden"
            style={{ cursor: dragging ? "grabbing" : zoom > 1 ? "grab" : "default" }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            <img
              src={url}
              alt={doc.file_name}
              draggable={false}
              className="max-w-none select-none transition-transform"
              style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
            />
          </div>
        )}

        {!loading && isDicom && (
          <div className="bg-gray-900 rounded-2xl p-8 space-y-4 max-w-md w-full mx-4">
            <div className="flex items-center gap-3">
              <span className="text-4xl">🩻</span>
              <div>
                <p className="text-white font-medium">DICOM file</p>
                <p className="text-gray-400 text-sm">In-browser DICOM viewer is coming in a future release</p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Filename</span><span className="text-gray-200 truncate ml-4 max-w-[200px]">{doc.file_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Size</span><span className="text-gray-200">{fmtSize(doc.file_size_bytes)}</span></div>
              {doc.source_name && <div className="flex justify-between"><span className="text-gray-500">Source</span><span className="text-gray-200">{doc.source_name}</span></div>}
              {doc.test_date && <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="text-gray-200">{fmtDate(doc.test_date)}</span></div>}
            </div>
            {url && (
              <a
                href={url}
                download={doc.file_name}
                className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2.5 text-sm font-medium transition-colors"
              >
                Download DICOM file
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Document Card ────────────────────────────────────────────────────────────

interface CardProps {
  doc:       PatientDocument;
  selected:  boolean;
  onSelect:  () => void;
  onView:    () => void;
  onDelete:  () => void;
  onToggleSensitive: () => void;
  categories: DocumentCategory[];
  isAdmin:   boolean;
  canDelete: boolean;
}

const DocumentCard: React.FC<CardProps> = ({ doc, selected, onSelect, onView, onDelete, onToggleSensitive, categories, isAdmin, canDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const catName = categories.find((c) => c.category_id === doc.category_id)?.name ?? null;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  return (
    <div
      className={`relative group bg-white rounded-xl border transition-all cursor-pointer
        ${selected ? "border-blue-400 shadow-[0_0_0_3px_rgba(59,130,246,0.15)]" : "border-slate-100 hover:border-slate-200 hover:shadow-sm"}`}
    >
      {/* Checkbox */}
      <div
        className="absolute top-3 left-3 z-10"
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
      >
        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
          selected ? "bg-blue-600 border-blue-600" : "border-slate-300 bg-white group-hover:border-slate-400"
        }`}>
          {selected && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </div>
      </div>

      {/* Card body */}
      <div className="p-4 pl-9" onClick={onView}>
        {/* Icon / thumb */}
        <div className="w-full h-24 rounded-lg bg-slate-50 flex items-center justify-center mb-3 overflow-hidden">
          <span className="text-4xl">{mimeIcon(doc.mime_type, doc.file_name)}</span>
        </div>

        <p className="text-sm font-medium text-slate-800 truncate leading-tight">{doc.file_name}</p>
        {catName && <p className="text-xs text-blue-600 mt-0.5">{catName}</p>}
        <p className="text-xs text-slate-400 mt-1">
          {doc.visit_id
            ? `Uploaded during visit on ${fmtDateTime(doc.visit_date || doc.uploaded_at)}`
            : `Uploaded on ${fmtDateTime(doc.uploaded_at || doc.test_date)}`}
        </p>
        <p className="text-xs text-slate-400">{fmtSize(doc.file_size_bytes)}</p>

        {/* Badges */}
        <div className="flex flex-wrap gap-1 mt-2">
          {doc.is_sensitive && (
            <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium">Sensitive</span>
          )}
          {doc.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-[10px] bg-slate-50 text-slate-500 border border-slate-100 px-1.5 py-0.5 rounded-full">{tag}</span>
          ))}
          {doc.tags.length > 2 && (
            <span className="text-[10px] bg-slate-50 text-slate-500 border border-slate-100 px-1.5 py-0.5 rounded-full">+{doc.tags.length - 2}</span>
          )}
        </div>
      </div>

      {/* Kebab menu */}
      <div className="absolute top-3 right-3" ref={menuRef} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => setMenuOpen((o) => !o)}
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
        </button>

        {menuOpen && (
          <div className="absolute right-0 top-8 w-44 bg-white border border-slate-100 rounded-xl shadow-lg py-1 z-20">
            <button
              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => { setMenuOpen(false); onView(); }}
            >View / Download</button>
            {isAdmin && (
              <button
                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => { setMenuOpen(false); onToggleSensitive(); }}
              >{doc.is_sensitive ? "Remove sensitive flag" : "Mark as sensitive"}</button>
            )}
            {canDelete && (
              <button
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                onClick={() => { setMenuOpen(false); onDelete(); }}
              >Delete</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main DocumentsTab ────────────────────────────────────────────────────────

interface Props {
  patientId: string;
  visitId?:  string;
  userRole?: string;
}

const DocumentsTab: React.FC<Props> = ({ patientId, visitId, userRole }) => {
  const [docs, setDocs]             = useState<PatientDocument[]>([]);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loading, setLoading]       = useState(true);

  const [q, setQ]               = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [dateFrom, setDateFrom]   = useState("");
  const [dateTo, setDateTo]       = useState("");

  const [showUpload, setShowUpload] = useState(false);
  const [viewerDoc, setViewerDoc]   = useState<PatientDocument | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [zipping, setZipping]       = useState(false);

  const isAdmin = userRole === "Admin";
  const canDelete = userRole === "Admin" || userRole === "Doctor";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: DocumentListFilters = {};
      if (catFilter) filters.category_id = catFilter;
      if (tagFilter) filters.tag = tagFilter;
      if (dateFrom)  filters.date_from = dateFrom;
      if (dateTo)    filters.date_to = dateTo;
      if (q)         filters.q = q;
      const data = await DocumentAPI.list(patientId, Object.keys(filters).length ? filters : undefined);
      setDocs(data);
      setSelectedIds(new Set()); // clear stale selections whenever the list refreshes
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [patientId, catFilter, tagFilter, dateFrom, dateTo, q]);

  useEffect(() => { load(); }, [load]);

  // Load categories once
  useEffect(() => {
    DocumentAPI.listCategories(patientId).then(setCategories).catch(() => {});
  }, [patientId]);

  const handleDelete = async (doc: PatientDocument) => {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return;
    try {
      await DocumentAPI.delete(patientId, doc.document_id);
      setDocs((d) => d.filter((x) => x.document_id !== doc.document_id));
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(doc.document_id); return next; });
      toast.success("Document deleted");
    } catch {
      toast.error("Failed to delete document");
    }
  };

  const handleToggleSensitive = async (doc: PatientDocument) => {
    try {
      const updated = await DocumentAPI.patchSensitivity(patientId, doc.document_id, !doc.is_sensitive);
      setDocs((d) => d.map((x) => x.document_id === updated.document_id ? updated : x));
    } catch {
      toast.error("Failed to update sensitivity");
    }
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allSelected = docs.length > 0 && selectedIds.size === docs.length;
  const toggleAll   = () => setSelectedIds(allSelected ? new Set() : new Set(docs.map((d) => d.document_id)));

  const handleZip = async () => {
    if (zipping || selectedIds.size === 0) return;
    setZipping(true);
    try {
      const res = await DocumentAPI.zip(patientId, Array.from(selectedIds));
      window.open(res.url, "_blank", "noopener,noreferrer");
      toast.success(
        `ZIP ready: ${res.file_count} file${res.file_count !== 1 ? "s" : ""}` +
        ` · ${(res.total_bytes / (1024 * 1024)).toFixed(1)} MB · link expires in ${res.expires_in / 60} min`
      );
    } catch (err: any) {
      // apiRequest always throws ApiError with .message normalised from the HTTP response
      toast.error(err?.message ?? "Failed to create ZIP");
    } finally {
      setZipping(false);
    }
  };

  const clearFilters = () => {
    setQ(""); setCatFilter(""); setTagFilter(""); setDateFrom(""); setDateTo("");
  };
  const hasFilters = q || catFilter || tagFilter || dateFrom || dateTo;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, notes, source…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white"
          />
        </div>

        {/* Category filter */}
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white text-slate-700"
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
        </select>

        {/* Tag filter */}
        <input
          type="text"
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          placeholder="Filter by tag…"
          className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white text-slate-700 w-36"
        />

        {/* Date range — kept in one flex group so the "to" label never orphans to its own line */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white text-slate-700" />
          <span className="text-slate-400 text-sm flex-shrink-0">to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white text-slate-700" />
        </div>

        {hasFilters && (
          <button onClick={clearFilters} className="text-xs text-slate-400 hover:text-slate-600 underline">Clear</button>
        )}

        <button
          onClick={() => setShowUpload(true)}
          className="ml-auto flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          Upload
        </button>
      </div>

      {/* Bulk-select bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5">
          <span className="text-sm text-blue-700 font-medium">{selectedIds.size} selected</span>
          <button
            onClick={handleZip}
            disabled={zipping}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {zipping ? (
              <>
                <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                Preparing…
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download ZIP
              </>
            )}
          </button>
          <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs text-slate-500 hover:text-slate-700">
            Deselect all
          </button>
        </div>
      )}

      {/* Document grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <svg className="animate-spin w-6 h-6 mr-2" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
          Loading documents…
        </div>
      ) : docs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400 space-y-2">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
          </svg>
          <p className="text-sm font-medium">{hasFilters ? "No documents match your filters" : "No documents yet"}</p>
          {!hasFilters && <p className="text-xs">Upload lab reports, scans, prescriptions, and more.</p>}
        </div>
      ) : (
        <div>
          {/* Select all */}
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={toggleAll}
              className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700"
            >
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${allSelected ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>
                {allSelected && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              {allSelected ? "Deselect all" : "Select all"}
            </button>
            <span className="text-xs text-slate-400">{docs.length} document{docs.length > 1 ? "s" : ""}</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {docs.map((doc) => (
              <DocumentCard
                key={doc.document_id}
                doc={doc}
                categories={categories}
                selected={selectedIds.has(doc.document_id)}
                onSelect={() => toggleSelect(doc.document_id)}
                onView={() => setViewerDoc(doc)}
                onDelete={() => handleDelete(doc)}
                onToggleSensitive={() => handleToggleSensitive(doc)}
                isAdmin={isAdmin}
                canDelete={canDelete}
              />
            ))}
          </div>
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <DocumentUploadModal
          patientId={patientId}
          visitId={visitId}
          categories={categories}
          onClose={() => setShowUpload(false)}
          onUploaded={load}
        />
      )}

      {/* Document viewer */}
      {viewerDoc && (
        <DocumentViewer
          doc={viewerDoc}
          patientId={patientId}
          onClose={() => setViewerDoc(null)}
        />
      )}
    </div>
  );
};

export default DocumentsTab;
